// src/components/RandevuListesi.js
//
// Randevuların tam listesi (K7). Takvim yalnızca tarihi olan ve iptal edilmemiş
// randevuları çizer; bu ekran koleksiyonun TAMAMINI okur, böylece
//  - İptal edilen randevular (takvimde gizli) sekmeden erişilebilir olur,
//  - 'start' alanı hiç olmayan v1 kayıtları "Tarihsiz" rozetiyle görünür kalır.
// Koleksiyon küçük olduğu için tek seferde okunur, sıralama/filtre client-side'dır.
//
// 2026-08-30 — SÜRESİ GEÇMİŞ sekmesi. Randevuyu kapatan TEK otomatik yol bağlı iş
// emrinin teslim edilmesiydi; iş emrine hiç dönüşmeyen randevu 'Bekleniyor'da
// sonsuza dek kalıyordu (canlı sayım: 14 kayıt, en eskisi 335 gün). Artık tarihi
// geçmiş açık randevular 'Bekleniyor'/'Onaylandı' sekmelerinden ÇIKARILIP kendi
// sekmesinde toplanır ve oradan toplu kapatılır — böylece durum sekmeleri yalnız
// gerçekten bekleyen işi gösterir.

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, doc, onSnapshot, writeBatch } from 'firebase/firestore';
import { normalizeTr, aramaMetniUret } from '../utils/search';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Paper, Typography, Tabs, Tab, TextField, InputAdornment, Chip, Stack,
    CircularProgress, Divider, Button, Alert, Checkbox, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { format, differenceInCalendarDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import {
    RANDEVU_DURUMLARI,
    normalizeRandevuDurum,
    getRandevuDurumColor,
    getRandevuStart,
    getRandevuEnd,
    randevuSuresiGecti
} from './AppointmentConstants';

// Sekme anahtarı — durum değeri DEĞİLDİR, yalnız bu ekrandaki görünüm filtresidir.
const SURESI_GECMIS = '__suresiGecmis__';

// Toplu kapatma yetkisi. Firestore kuralı appointments yazımını yöneticilere açar
// (firestore.rules: isManager); UI da aynı kümeyi gösterir.
const TOPLU_KAPATMA_ROLLERI = ['Genel Müdür', 'Müdür', 'Koordinatör'];

const formatTarih = (date) => {
    if (!date) return null;
    try { return format(date, 'dd MMM yyyy HH:mm', { locale: tr }); } catch { return null; }
};

const RandevuKarti = ({ randevu, secim = null }) => {
    const start = getRandevuStart(randevu);
    const end = getRandevuEnd(randevu);
    const plaka = randevu.aracPlaka || 'Plaka Yok';
    const marka = [randevu.aracMarka, randevu.aracModel].filter(Boolean).join(' ');
    const gecenGun = randevu._suresiGecti && (end || start)
        ? differenceInCalendarDays(new Date(), end || start)
        : null;

    const kart = (
        <Paper
            variant="outlined"
            component={RouterLink}
            to={`/randevu/${randevu.id}`}
            sx={{
                p: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
                textDecoration: 'none', color: 'inherit', '&:hover': { backgroundColor: 'action.hover' }
            }}
        >
            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                <Typography variant="body2" fontWeight="bold" noWrap>
                    {randevu.customerName || randevu.musteriAdSoyad || 'İsimsiz müşteri'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {plaka}{marka ? ` · ${marka}` : ''}
                </Typography>
            </Box>

            <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                {start ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatTarih(start)}{end ? ` → ${formatTarih(end)}` : ''}
                    </Typography>
                ) : (
                    <Chip size="small" color="warning" variant="outlined" label="Tarihsiz kayıt" />
                )}
                {randevu.serviceType && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {randevu.serviceType}
                    </Typography>
                )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                {gecenGun !== null && (
                    <Chip size="small" color="warning" label={`${gecenGun} gün geçti`} />
                )}
                {randevu.workOrderId && <Chip size="small" variant="outlined" label="İş emri var" />}
                <Chip size="small" color={getRandevuDurumColor(randevu.durum)} label={normalizeRandevuDurum(randevu.durum)} />
            </Stack>
        </Paper>
    );

    if (!secim) return kart;

    // Seçim kutusu kartın DIŞINDA: kartın kendisi RouterLink, içine konsaydı
    // her tik randevu detayına gitmeye çalışırdı.
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Checkbox
                checked={secim.secili}
                onChange={secim.onDegis}
                inputProps={{ 'aria-label': `${randevu.customerName || randevu.musteriAdSoyad || 'Randevu'} kaydını seç` }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>{kart}</Box>
        </Box>
    );
};

const RandevuListesi = () => {
    const { currentUser } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('Bekleniyor');
    const [arama, setArama] = useState('');
    const [secililer, setSecililer] = useState([]);
    const [kapatiliyor, setKapatiliyor] = useState(false);
    const [topluHata, setToplWHata] = useState('');

    useEffect(() => {
        // orderBy KULLANILMIYOR: 'start' alanı olmayan legacy kayıtlar sorgudan düşerdi.
        const unsub = onSnapshot(collection(db, 'appointments'), (snapshot) => {
            setAppointments(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
            setLoading(false);
        }, (error) => {
            console.error('Randevular çekilirken hata:', error);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const normalized = useMemo(() => (
        appointments
            .map(app => ({
                ...app,
                _durum: normalizeRandevuDurum(app.durum),
                _start: getRandevuStart(app),
                _suresiGecti: randevuSuresiGecti(app)
            }))
            .sort((a, b) => (b._start?.getTime() || 0) - (a._start?.getTime() || 0))
    ), [appointments]);

    // Süresi geçmiş kayıt kendi sekmesine taşınır; 'Bekleniyor'/'Onaylandı'
    // sekmelerinde İKİNCİ KEZ görünmez (sayaçlar da bu kurala uyar).
    const sayilar = useMemo(() => {
        const s = { [SURESI_GECMIS]: 0 };
        RANDEVU_DURUMLARI.forEach(d => { s[d] = 0; });
        normalized.forEach(app => {
            if (app._suresiGecti) s[SURESI_GECMIS] += 1;
            else s[app._durum] = (s[app._durum] || 0) + 1;
        });
        return s;
    }, [normalized]);

    // G8: Türkçe-normalize (İ/ı/ş güvenli) çok alanlı arama havuzu.
    const gorunen = useMemo(() => {
        const q = normalizeTr(arama).replace(/\s/g, '');
        return normalized
            .filter(app => (tab === SURESI_GECMIS ? app._suresiGecti : (!app._suresiGecti && app._durum === tab)))
            .filter(app => {
                if (!q) return true;
                const havuz = aramaMetniUret(
                    app.customerName, app.musteriAdSoyad, app.customerPhone,
                    app.aracPlaka, app.aracMarka, app.aracModel, app.serviceType
                ).replace(/\s/g, '');
                return havuz.includes(q);
            });
    }, [normalized, tab, arama]);

    const tarihsizSayisi = normalized.filter(a => !a._start).length;
    const topluKapatabilir = TOPLU_KAPATMA_ROLLERI.includes(currentUser?.role);
    const gorunenIdler = gorunen.map(a => a.id);
    const secililerGorunen = secililer.filter(id => gorunenIdler.includes(id));
    const hepsiSecili = gorunenIdler.length > 0 && secililerGorunen.length === gorunenIdler.length;

    const sekmeDegis = (yeniTab) => { setTab(yeniTab); setSecililer([]); setToplWHata(''); };
    const secimDegis = (id) => setSecililer(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    const hepsiniSec = () => setSecililer(hepsiSecili ? [] : gorunenIdler);

    // Toplu kapatma: yalnız DURUM yazılır. Randevu silinmez, iş emri bağı ve tarih
    // aynen kalır; yanlışlıkla kapatılan kayıt Randevu Detayı'ndan geri açılabilir.
    const topluKapat = async (yeniDurum) => {
        if (secililerGorunen.length === 0) return;
        const onay =
            `${secililerGorunen.length} randevu "${yeniDurum}" olarak işaretlenecek.

` +
            `Kayıtlar SİLİNMEZ; takvimden ve bekleyen listelerden düşer, ` +
            `Randevu Detayı ekranından geri alınabilir.

Devam edilsin mi?`;
        if (!window.confirm(onay)) return;

        setKapatiliyor(true);
        setToplWHata('');
        try {
            // Firestore batch sınırı 500; liste bundan büyük olursa parçalanır.
            for (let i = 0; i < secililerGorunen.length; i += 400) {
                const parca = secililerGorunen.slice(i, i + 400);
                const batch = writeBatch(db);
                parca.forEach(id => batch.update(doc(db, 'appointments', id), { durum: yeniDurum }));
                await batch.commit();
            }
            setSecililer([]);
        } catch (error) {
            console.error('Toplu randevu kapatma hatası:', error);
            setToplWHata(error?.message || 'Randevular kapatılırken bir hata oluştu.');
        } finally {
            setKapatiliyor(false);
        }
    };

    if (loading) return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EventIcon color="primary" fontSize="large" />
                    <Box>
                        <Typography variant="h4" component="h1">Randevu Listesi</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Toplam {normalized.length} randevu
                            {sayilar[SURESI_GECMIS] > 0 && ` · ${sayilar[SURESI_GECMIS]} tanesinin süresi geçmiş`}
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Müşteri, plaka veya hizmet ara…"
                        value={arama}
                        onChange={(e) => setArama(e.target.value)}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
                    />
                    <Button variant="outlined" startIcon={<CalendarMonthIcon />} component={RouterLink} to="/randevular">
                        Takvim
                    </Button>
                </Stack>
            </Box>

            {tarihsizSayisi > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {tarihsizSayisi} randevunun tarih bilgisi eksik. Bu kayıtlar takvimde çizilemez; burada
                    “Tarihsiz kayıt” rozetiyle listelenir.
                </Alert>
            )}

            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Tabs
                    value={tab}
                    onChange={(e, v) => sekmeDegis(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {RANDEVU_DURUMLARI.map(d => (
                        <Tab key={d} value={d} label={`${d} (${sayilar[d] || 0})`} />
                    ))}
                    <Tab
                        value={SURESI_GECMIS}
                        icon={<HistoryToggleOffIcon fontSize="small" />}
                        iconPosition="start"
                        label={`Süresi Geçmiş (${sayilar[SURESI_GECMIS] || 0})`}
                        sx={{ color: sayilar[SURESI_GECMIS] > 0 ? 'warning.main' : undefined, minHeight: 48 }}
                    />
                </Tabs>
                <Divider sx={{ mb: 2 }} />

                {tab === SURESI_GECMIS && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                            Tarihi geçtiği hâlde hâlâ açık (Bekleniyor/Onaylandı) randevular. İş emrine
                            dönüşen randevular araç teslim edilince kendiliğinden kapanır; bu kayıtlar
                            iş emrine dönüşmemiş, bu yüzden elle kapatılması gerekir.
                            {!topluKapatabilir && ' Toplu kapatma yetkisi Genel Müdür, Müdür ve Koordinatör rollerindedir.'}
                        </Alert>

                        {topluHata && <Alert severity="error" sx={{ mb: 1.5 }}>{topluHata}</Alert>}

                        {topluKapatabilir && gorunen.length > 0 && (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Tooltip title="Listedeki tüm kayıtları seç">
                                    <Button size="small" onClick={hepsiniSec} disabled={kapatiliyor}>
                                        {hepsiSecili ? 'Seçimi kaldır' : 'Tümünü seç'}
                                    </Button>
                                </Tooltip>
                                <Typography variant="body2" color="text.secondary">
                                    {secililerGorunen.length} kayıt seçili
                                </Typography>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={secililerGorunen.length === 0 || kapatiliyor}
                                    onClick={() => topluKapat('İptal')}
                                >
                                    Seçilenleri İptal Et
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={secililerGorunen.length === 0 || kapatiliyor}
                                    onClick={() => topluKapat('Tamamlandı')}
                                >
                                    Seçilenleri Tamamlandı İşaretle
                                </Button>
                            </Stack>
                        )}
                    </Box>
                )}

                <Stack spacing={1.5}>
                    {gorunen.length > 0 ? (
                        gorunen.map(app => (
                            <RandevuKarti
                                key={app.id}
                                randevu={app}
                                secim={tab === SURESI_GECMIS && topluKapatabilir
                                    ? { secili: secililer.includes(app.id), onDegis: () => secimDegis(app.id) }
                                    : null}
                            />
                        ))
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            {tab === SURESI_GECMIS ? 'Süresi geçmiş açık randevu yok.' : 'Bu sekmede randevu yok.'}
                        </Typography>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
};

export default RandevuListesi;
